import { ArrowLeft, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { PdfLocale } from "../../utils/templateFormPdf";
import { cn } from "@/lib/utils";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import type { SavedTemplateForm } from "../../hooks/useSavedTemplateForms";
import {
  SavedFormPicker,
  type SavedFormsProjectChoice,
  type SavedFormsState,
} from "./SavedFormPicker";

/** The chrome every template form shares: header bar, the document number /
 *  name row above the sections, and the output options above Save. */

interface TemplateFormHeaderProps {
  copy: TemplateFormTranslations;
  templateName: string;
  onBack: () => void;
  /** Dismisses the whole flow. The dialog's own close button is suppressed —
   *  it would land on top of Open PDF — so the popup needs this one. */
  onClose: () => void;
  closeLabel: string;
}

export function TemplateFormHeader({
  copy,
  templateName,
  onBack,
  onClose,
  closeLabel,
}: TemplateFormHeaderProps) {
  return (
    <header className="flex items-center gap-2 border-b px-4 py-3 sm:px-6">
      <Button type="button" variant="ghost" size="icon" aria-label={copy.back} onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <DialogTitle className="min-w-0 truncate text-base">{templateName}</DialogTitle>
      <div className="ml-auto flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" aria-label={closeLabel} onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}

interface TemplateFormMetaRowProps {
  copy: TemplateFormTranslations;
  /** Shown, never typed — the database hands out the real number as the
   *  document is filed, so this is a prediction and the field says so. */
  documentNumber: string;
  documentName: string;
  onNameChange: (value: string) => void;
  /** This template's already-filed forms, to start from one of them. */
  savedForms: SavedFormsState;
  /** Set only at building scope, where the picker has to ask which project's
   *  filed forms to draw on before it can list any. */
  savedFormsProject?: SavedFormsProjectChoice;
  onChoosePrefilled: (form: SavedTemplateForm) => void;
}

export function TemplateFormMetaRow({
  copy,
  documentNumber,
  documentName,
  onNameChange,
  savedForms,
  savedFormsProject,
  onChoosePrefilled,
}: TemplateFormMetaRowProps) {
  return (
    <div className="pb-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-20 space-y-1.5">
          <Label htmlFor="template-form-number">{copy.documentNumber}</Label>
          {/* `readOnly`, not `disabled`: the value is real and worth reading,
              it just isn't yours to set. Numbering is a database guarantee
              (`next_template_form_number` under a unique index), reserved as
              the document is filed — an editable field here could only ever
              display a number different from the one that gets used. */}
          <Input
            id="template-form-number"
            inputMode="numeric"
            value={documentNumber}
            readOnly
            aria-describedby="template-form-number-hint"
            className="bg-muted text-muted-foreground"
          />
        </div>
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          <Label htmlFor="template-form-name">{copy.documentName}</Label>
          <Input
            id="template-form-name"
            value={documentName}
            onChange={(event) => onNameChange(event.target.value)}
          />
        </div>
        <SavedFormPicker
          copy={copy}
          forms={savedForms}
          projectChoice={savedFormsProject}
          searchPlaceholder={copy.searchSavedForms}
          emptyLabel={copy.noSavedForms}
          onPick={onChoosePrefilled}
          trigger={
            <Button type="button" variant="outline" size="sm">
              {copy.choosePrefilled}
            </Button>
          }
        />
      </div>
      {/* Visible rather than a `title`: a tooltip explaining why a field cannot
          be typed in is no explanation at all on a touch device. */}
      <p id="template-form-number-hint" className="mt-1.5 text-xs text-muted-foreground">
        {copy.documentNumberHint}
      </p>
    </div>
  );
}

interface TemplateFormFooterProps {
  copy: TemplateFormTranslations;
  attachmentsInPdf: boolean;
  signDigitally: boolean;
  onAttachmentsInPdfChange: (checked: boolean) => void;
  onSignDigitallyChange: (checked: boolean) => void;
  /** Save is gated while the form is not yet fileable. */
  saveDisabled: boolean;
  /** Why, when it is — put on the button so it is never a dead control. */
  saveBlockedReason?: string;
  /** True while the save is uploading and rendering. */
  saving: boolean;
  /** Set when this user's organisation cannot sign — the toggle says why. */
  signingUnavailableReason?: string;
  /** The language the document itself prints in. */
  pdfLocale: PdfLocale;
  onPdfLocaleChange: (locale: PdfLocale) => void;
  /** Renders the form as it prints and shows it. Sits by Save because it is
   *  the same act at a different commitment — see what you are about to file. */
  onPreview: () => void;
  onSave: () => void;
}

/** Ties the visible refusal to the button it refuses on. */
const SAVE_REASON_ID = "template-form-save-reason";

export function TemplateFormFooter({
  copy,
  attachmentsInPdf,
  signDigitally,
  onAttachmentsInPdfChange,
  onSignDigitallyChange,
  saveDisabled,
  saveBlockedReason,
  saving,
  signingUnavailableReason,
  pdfLocale,
  onPdfLocaleChange,
  onPreview,
  onSave,
}: TemplateFormFooterProps) {
  // Save sits on the right edge, level with the output toggles rather than on a
  // line of its own — the form is in a height-capped dialog, so the row it saves
  // is a row the fields get back. It wraps below the toggles when the footer is
  // too narrow to hold both.
  return (
    <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t px-4 py-4 sm:px-6">
      <div className="space-y-2">
        <FormToggle
          id="template-form-attachments-in-pdf"
          label={copy.attachToPdf}
          explanation={copy.outputHints.attachToPdf}
          checked={attachmentsInPdf}
          onChange={onAttachmentsInPdfChange}
        />
        <FormToggle
          id="template-form-sign-digitally"
          label={copy.signDigitally}
          explanation={copy.outputHints.signDigitally}
          checked={signDigitally}
          onChange={onSignDigitallyChange}
          disabled={Boolean(signingUnavailableReason)}
          hint={signingUnavailableReason}
        />
      </div>
      {/* Language, then Open PDF, then Save — reading left to right as
          "in which language", "show me", "file it". */}
      <div className="flex flex-wrap items-center gap-2">
        {/* The statutory forms are filed in Estonian, so the document's own
            language is chosen here rather than following the interface. */}
        <Label htmlFor="template-form-pdf-language" className="font-normal text-muted-foreground">
          {copy.pdfLanguage.label}
        </Label>
        <Select value={pdfLocale} onValueChange={(value) => onPdfLocaleChange(value as PdfLocale)}>
          <SelectTrigger id="template-form-pdf-language" className="h-9 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="et">{copy.pdfLanguage.et}</SelectItem>
            <SelectItem value="en">{copy.pdfLanguage.en}</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" className="gap-1.5" onClick={onPreview}>
          <FileText aria-hidden className="h-4 w-4" />
          {copy.preview}
        </Button>
        <Button
          type="button"
          disabled={saveDisabled || saving}
          // A disabled button fires no pointer events, so a tooltip could not
          // open, and `title` is invisible on touch besides. The reason is
          // rendered below instead; this only repeats it for a hovering mouse.
          title={saveDisabled ? saveBlockedReason : undefined}
          aria-describedby={saveDisabled && saveBlockedReason ? SAVE_REASON_ID : undefined}
          onClick={onSave}
        >
          {saving ? copy.saving : copy.save}
        </Button>
      </div>
      {/* Why Save is unavailable, said out loud. `missingFields` names the
          fields, which is the whole point — on the diary the first empty one
          sits below the fold, so a reason nobody can see is no reason at all.
          Full width so it keeps its own line under the footer's two groups. */}
      {saveDisabled && saveBlockedReason && (
        <p id={SAVE_REASON_ID} className="w-full text-right text-xs text-destructive">
          {saveBlockedReason}
        </p>
      )}
    </footer>
  );
}

interface FormToggleProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Why the toggle is unavailable — shown beside it rather than left implicit. */
  hint?: string;
  /** What ticking it concretely does, on hover. The labels alone read as
   *  promises ("and digital document"), so the detail has to be reachable. */
  explanation: string;
}

function FormToggle({
  id,
  label,
  checked,
  onChange,
  disabled,
  hint,
  explanation,
}: FormToggleProps) {
  const explanationId = `${id}-explanation`;

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        {/* The whole box-and-label pair is the hover target, so the explanation
            is not hidden behind a 16px tick box. */}
        <TooltipTrigger asChild>
          <span className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={checked}
              disabled={disabled}
              // Radix only wires aria-describedby while the tooltip is open, and
              // a disabled checkbox never opens one — this keeps the explanation
              // available to assistive tech either way.
              aria-describedby={explanationId}
              onCheckedChange={(value) => onChange(value === true)}
            />
            <Label htmlFor={id} className={cn("font-normal", disabled && "text-muted-foreground")}>
              {label}
            </Label>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">{explanation}</TooltipContent>
      </Tooltip>
      <span id={explanationId} className="sr-only">
        {explanation}
      </span>
      {hint && <span className="text-xs text-muted-foreground">({hint})</span>}
    </div>
  );
}
